import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { BackLink } from 'compositions/BackLink'
import { ConfirmDialog } from 'compositions/ConfirmDialog'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { Page } from 'compositions/Page'
import { useContact, useDeleteTemplate, useLayout, useTemplate, useUpdateTemplate } from 'hooks/api'
import { getApiErrorMessage } from 'hooks/useFetchApi'
import { Pencil, Save, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import type { TemplateDetail as TemplateDetailType } from 'types/templates'

import { LayoutSelector } from './layout-selector'
import type { TemplateMode } from './template-editor'
import { type TemplateFormData, TemplateFormFields } from './template-form-fields'
import { TemplatePreview } from './template-preview'
import {
  buildVariablesRecord,
  DEFAULT_STATE,
  TemplateVariables,
  type TemplateVariableState,
} from './template-variables'
function detectMode(body: string): TemplateMode {
  return /<\w[\s\S]*?>/.test(body) ? 'html' : 'plaintext'
}

function templateToFormData(template: TemplateDetailType): TemplateFormData {
  return {
    name: template.name,
    slug: template.slug,
    subject: template.subject ?? '',
    body: template.body,
    mode: detectMode(template.body),
  }
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value || '-'}</dd>
    </div>
  )
}

function TemplateDetailContent({ template, refetch }: { template: TemplateDetailType | null; refetch: () => void }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { perform: updateTemplate, loading: saving } = useUpdateTemplate()
  const { perform: deleteTemplate } = useDeleteTemplate()
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    slug: '',
    subject: '',
    body: '',
    mode: 'plaintext',
  })
  const [layoutId, setLayoutId] = useState<string | null>(null)
  const [varState, setVarState] = useState<TemplateVariableState>(DEFAULT_STATE)
  const { data: previewContact } = useContact(varState.contactId ?? undefined)
  const variablesRecord = useMemo(
    () => buildVariablesRecord(varState, previewContact ?? null),
    [varState, previewContact],
  )

  // Fetch the selected layout's body for preview
  const { data: layoutDetail } = useLayout(layoutId ?? undefined)
  // Use the layout body from the template detail if it matches, otherwise from the fetched layout
  const currentLayoutBody = template?.layout?.id === layoutId ? template?.layout?.body : layoutDetail?.body

  useEffect(() => {
    if (template) {
      setFormData(templateToFormData(template))
      setLayoutId(template.layoutId)
    }
  }, [template])

  const handleFieldChange = useCallback((field: keyof TemplateFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const startEditing = useCallback(() => {
    if (template) {
      setFormData(templateToFormData(template))
      setLayoutId(template.layoutId)
    }
    setEditing(true)
  }, [template])

  const cancelEditing = useCallback(() => {
    if (template) {
      setFormData(templateToFormData(template))
      setLayoutId(template.layoutId)
    }
    setEditing(false)
  }, [template])

  const handleSave = useCallback(async () => {
    if (!id) {
      return
    }
    try {
      await updateTemplate(id, {
        name: formData.name,
        subject: formData.subject.trim() || null,
        body: formData.body,
        layoutId,
      })
      refetch()
      setEditing(false)
    } catch (err) {
      toast.error('Failed to save template', { description: getApiErrorMessage(err) })
    }
  }, [id, formData, layoutId, updateTemplate, refetch])

  const handleDelete = useCallback(async () => {
    if (!id) {
      return
    }
    try {
      await deleteTemplate(id)
      navigate('/templates')
    } catch (err) {
      toast.error('Failed to delete template', { description: getApiErrorMessage(err) })
    }
  }, [id, deleteTemplate, navigate])

  if (!template) {
    return <div className="py-12 text-center text-muted-foreground">Template not found</div>
  }

  const currentBody = editing ? formData.body : template.body

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      {/* Editor panel */}
      <Card className="min-w-0 flex-1">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle>{template.name}</CardTitle>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {editing ? (
              <>
                <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving}>
                  <X className="mr-1 h-4 w-4" />
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  <Save className="mr-1 h-4 w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={startEditing}>
                  <Pencil className="mr-1 h-4 w-4" />
                  Edit
                </Button>
                <ConfirmDialog
                  title="Delete template"
                  description={`Are you sure you want to delete "${template.name}"? This action cannot be undone.`}
                  onConfirm={handleDelete}
                  trigger={
                    <Button variant="destructive" size="sm">
                      <Trash2 className="mr-1 h-4 w-4" />
                      Delete
                    </Button>
                  }
                />
              </>
            )}
          </div>
        </CardHeader>

        <Separator />

        <CardContent className="pt-6">
          {editing ? (
            <>
              <TemplateFormFields data={formData} onChange={handleFieldChange} slugLocked />
              <div className="mt-4">
                <LayoutSelector value={layoutId} onChange={setLayoutId} />
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-4">
              <InfoRow label="Name" value={template.name} />
              <InfoRow label="Subject" value={template.subject} />
              <InfoRow label="Layout" value={template.layout?.name ?? 'None'} />
              <div className="space-y-0.5">
                <dt className="text-sm font-medium text-muted-foreground">Template Body</dt>
                <dd className="break-all whitespace-pre-wrap rounded-md bg-muted/30 px-3 py-2 font-mono text-sm">
                  {template.body || '-'}
                </dd>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview + Variables */}
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <TemplatePreview
          body={currentBody}
          subject={editing ? formData.subject : (template.subject ?? '')}
          mode={formData.mode}
          variables={variablesRecord}
          layoutBody={currentLayoutBody}
        />
        <TemplateVariables state={varState} onChange={setVarState} templateBody={currentBody} />
      </div>
    </div>
  )
}

export function TemplateDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: template, loading, refetch } = useTemplate(id)

  if (!loading && !template) {
    return (
      <Page>
        <div className="py-12 text-center text-muted-foreground">Template not found</div>
      </Page>
    )
  }

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/templates">Back to Templates</BackLink>
      </div>

      <LoadingWrapper
        loading={loading}
        loadingElement={
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <Separator />
            <CardContent className="space-y-4 pt-6">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        }>
        <TemplateDetailContent template={template} refetch={refetch} />
      </LoadingWrapper>
    </Page>
  )
}
